import React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { useMyUnits } from '~/data/orgunits';
import { StoreState } from '~/reducer';
import {
  orgUnitsStoreStateSelector,
  surveyStoreStateSelector,
} from '~/selectors';
import { AppDispatch } from '~/store';

import {
  closeAnswerAndResultsDialog,
  fetchFilteredSurveyResults,
  fetchSurveyAnswers,
  fetchSurveyResults,
} from '../slice';
import { ChildInput, SurveyResultsObject, UnitType } from '../types';

import { exportSurveyResultsToExcel } from './ExportSurveyResults';

export type UseSurveyResultsDialogOutput = {
  open: boolean;
  onDialogClose: () => void;
  print: () => void;
  surveyResults: SurveyResultsObject;
  menuList: ChildInput[];
  selectValue: number;
  selectOnChange: (value: number) => void;
  isLoading: boolean;
  failedToLoad: boolean;
  exportResults: () => void;
};

export const useSurveyResults = (
  resultId: string,
  handleResultsClose: () => void,
): UseSurveyResultsDialogOutput => {
  const { surveyReviewers, surveyQuestions, surveyRawResults, surveyResults } =
    useSelector((state: StoreState) => {
      return surveyStoreStateSelector(state);
    });
  const { isLoading, failedToLoad } = useSelector((state: StoreState) => {
    return orgUnitsStoreStateSelector(state);
  });
  const [selectValue, setSelectValue] = useState<number>(-1);
  const { t } = useTranslation();
  const allLabel = t('label_all_units');
  const { myUnits } = useMyUnits();

  const unitList = myUnits
    .filter((team) =>
      !!surveyReviewers
        ? surveyReviewers.some((r: UnitType) => r.id === team.fOrgUnitId)
        : true,
    )
    .map((unit) => {
      return {
        value: unit.fOrgUnitId,
        label: unit.fOrgUnitName,
      };
    }) as ChildInput[];

  const dispatch: AppDispatch = useDispatch();
  const [open, setOpen] = useState<boolean>(false);

  const onDialogClose = React.useCallback(() => {
    handleResultsClose();
    setOpen(false);
    (async () => {
      await dispatch(closeAnswerAndResultsDialog());
    })();
  }, [handleResultsClose, dispatch]);

  const print = () => {
    window.print();
  };

  useEffect(() => {
    setOpen(Boolean(resultId));
  }, [resultId]);

  useEffect(() => {
    (async () => {
      if (selectValue === -1) {
        await dispatch(fetchSurveyResults(resultId));
      } else {
        await dispatch(
          fetchFilteredSurveyResults({ id: resultId, unitId: selectValue }),
        );
      }
    })();
  }, [dispatch, resultId, selectValue]);

  useEffect(() => {
    (async () => {
      await dispatch(fetchSurveyAnswers(resultId));
    })();
  }, [dispatch, resultId]);

  const selectOnChange = (value: number) => {
    setSelectValue(value);
  };

  const exportResults = () => {
    exportSurveyResultsToExcel(surveyQuestions, surveyRawResults);
  };

  return {
    surveyResults,
    print,
    open,
    onDialogClose,
    menuList: [{ label: allLabel, value: -1 }, ...unitList],
    selectValue,
    selectOnChange,
    isLoading,
    failedToLoad,
    exportResults,
  };
};
